import { graphql } from '@/service/graphql'
import { type SanitizedResult } from '@amzn/amp-gql'
import { gqlFetch } from '@/service/gql'
import { getAuth } from '@/auth/authRef'
import { ctx } from '@/util/context'
import { logDebug, type Maybe } from '@amazon/vinyl-util'
import {
    type TrackLikesContext,
    trackLikesContextFrag,
} from '@/service/fragments/trackLikesContextFrag'
import { createToggleGeneration, runOptimisticToggle } from '@/service/toggleGeneration'
import { trackLikeOverlay } from '@/track/trackLikeOverlay'

export type LikeState = 'LIKE' | 'DISLIKE' | 'NEUTRAL'

export type { TrackLikesContext }

const likeTrackQuery = graphql(
    `
        mutation setTrackLikeState(
            $likeState: String
            $trackId: String!
            $isAuthenticated: Boolean!
        ) {
            setTrackLikeState(likeState: $likeState, trackId: $trackId) {
                likeState
                track {
                    ...trackLikesContext
                }
            }
        }
    `,
    [trackLikesContextFrag]
)

export type LikeTrackQueryResult = SanitizedResult<typeof likeTrackQuery>

export interface TrackLikeOptions {
    readonly trackId: string
    readonly likeState: LikeState
}

const { target } = ctx('trackLikeService')

/**
 * Persist a track like/dislike to the gateway. The optimistic overlay
 * lives in `toggleTrackLike` — this call is the pure server write, so a
 * fresh read may still echo the pre-mutation `liked` for the gateway's
 * cache window until the overlay's TTL reconciles it.
 */
export async function setTrackLikeState(options: TrackLikeOptions): Promise<LikeTrackQueryResult> {
    logDebug(target, options)
    return gqlFetch(likeTrackQuery, {
        likeState: options.likeState,
        trackId: options.trackId,
        isAuthenticated: getAuth().isSignedIn(),
    })
}

export interface ToggleTrackLikeResult {
    readonly trackId: string
    /** The liked state the toggle switched TO. */
    readonly liked: boolean
    /** Settles with the mutation. */
    readonly done: Promise<LikeTrackQueryResult>
}

const toggleGeneration = createToggleGeneration()

/**
 * Flip the like state of `track` off the caller-provided current `liked`
 * and fire the save. Returns null when the track has no id to mutate. The
 * flip is written to the normalized cache immediately so every mounted
 * `TrackLikeButton` — the detail hero, list/feed rows, and the transport /
 * full-player controls — reflects it in the same tick, since each button
 * reads the track's live normalized-cache entry. A failed mutation reverts
 * the overlay to the pre-flip state so the UI never lies about a decision
 * the server rejected — unless a newer toggle has since superseded it.
 */
export function toggleTrackLike(track: Maybe<TrackLikesContext>): ToggleTrackLikeResult | null {
    const trackId = track?.id
    if (!trackId) return null
    const prevLiked = track.likesContext?.liked ?? false
    const liked = !prevLiked
    const done = runOptimisticToggle({
        toggleGeneration,
        id: trackId,
        applyNext: () => optimisticTrackLikeUpdate(trackId, liked),
        applyPrev: () => optimisticTrackLikeUpdate(trackId, prevLiked),
        save: () => setTrackLikeState({ trackId, likeState: liked ? 'LIKE' : 'NEUTRAL' }),
    })
    return { trackId, liked, done }
}

/**
 * Remember `liked` for a track and reflect it across every mounted button:
 * writes the durable localStorage overlay AND the in-memory normalized-cache
 * overlay, both on the same TTL, so the like survives a reload (re-armed on boot
 * by `reapplyPersistedTrackLikes`) and out-ranks a stale server read.
 */
export function optimisticTrackLikeUpdate(trackId: string, liked: boolean): void {
    trackLikeOverlay.write(trackId, liked)
}
